import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import { TransitionProps } from '@mui/material/transitions';
import Fade from '@mui/material/Fade';
import Button from '../Button';
import Dialog from '../Dialog';
import FashionUnited from '../Icons/FashionUnited';
import { NavTabs, Tab } from '../Tabs';

const CookieConsentButton = styled(Button)`
  && {
    height: unset;
    min-height: 40px;
    min-width: 80px;
    font-size: var(--size-caption);
  }
`;

const CookieConsentButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;
  margin: 0.5rem 0;
  width: 100%;

  @media (min-width: 600px) {
    flex-direction: row;
  }
`;

const CookieConsentLogoContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const NoTransitionComponent = React.forwardRef((
  properties: TransitionProps & {
    children: React.ReactElement;
  },
  reference: React.Ref<unknown>,
) => <Fade appear={false} ref={reference} {...properties} />);

const cookieConsentPropertyTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.node.isRequired,
  message: PropTypes.node.isRequired,
  grantButtonText: PropTypes.node.isRequired,
  denyButtonText: PropTypes.node.isRequired,
  onGrant: PropTypes.func.isRequired,
  onDeny: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired).isRequired,
  currentTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
};

type CookieConsentProperties = InferProps<
  typeof cookieConsentPropertyTypes
>;

const CookieConsent = ({
  open,
  title,
  message,
  grantButtonText,
  denyButtonText,
  onGrant,
  onDeny,
  tabs,
  currentTab,
  onTabChange,
} : CookieConsentProperties) => (
  <Dialog
    open={open}
    TransitionComponent={NoTransitionComponent}
    titleNode={(
      <>
        <CookieConsentLogoContainer>
          <FashionUnited width="200px" height="50px" />
        </CookieConsentLogoContainer>
        <hr />
        {title}
      </>
    )}
    content={message}
    tabs={(
      <NavTabs
        value={currentTab}
        tabs={tabs}
        onTabChange={onTabChange}
        NavTab={Tab}
      />
    )}
    actions={(
      <CookieConsentButtonGroup>
        <CookieConsentButton
          variant="outlined"
          onClick={onDeny}
          fullWidth
        >
          {denyButtonText}
        </CookieConsentButton>
        <CookieConsentButton
          variant="contained"
          onClick={onGrant}
          fullWidth
        >
          {grantButtonText}
        </CookieConsentButton>
      </CookieConsentButtonGroup>
    )}
  />
);

CookieConsent.propTypes = cookieConsentPropertyTypes;

export default CookieConsent;
